import Config from "@via/utils/config";

const STORAGE_KEY = "vf.postLoginRedirect";

function getNormalizedBasename(): string {
  const basename = Config.ROUTER_BASENAME || "";
  if (!basename) return "";
  return basename === "/" ? "" : basename.replace(/\/+$/, "");
}

// A browser reads a backslash as a slash and drops tabs and newlines from a URL, so a
// value carrying a backslash or any control character can change meaning between this
// check and the navigation.
function hasUnsafeCharacter(path: string): boolean {
  for (let i = 0; i < path.length; i++) {
    const code = path.charCodeAt(i);
    if (code === 0x5c || code <= 0x1f || code === 0x7f) return true;
  }
  return false;
}

/**
 * The in-app path to return to, with the router basename removed, or null when the
 * value is not a path that can only ever lead back into this origin. The value that
 * is returned is the one checked, because it is the one consumers navigate to.
 */
export function normalizeRedirectPath(
  path: string | null | undefined
): string | null {
  if (!isSafeRedirectPath(path)) return null;

  const basename = getNormalizedBasename();
  if (!basename) return path;

  let stripped = path;
  if (path === basename) stripped = "/";
  else if (path.startsWith(`${basename}/`))
    stripped = path.slice(basename.length);
  else if (path.startsWith(`${basename}?`) || path.startsWith(`${basename}#`))
    stripped = `/${path.slice(basename.length)}`;

  return isSafeRedirectPath(stripped) ? stripped : null;
}

export function isSafeRedirectPath(
  path: string | null | undefined
): path is string {
  if (!path) return false;
  if (!path.startsWith("/")) return false;
  if (path.startsWith("//")) return false;
  if (hasUnsafeCharacter(path)) return false;
  if (path === "/login" || path.startsWith("/login?")) return false;
  if (/\/[a-zA-Z][a-zA-Z\d+\-.]*:/.test(path)) return false;
  return resolvesToThisOrigin(path);
}

function resolvesToThisOrigin(path: string): boolean {
  const { origin } = window.location;
  try {
    const url = new URL(path, origin);
    // "/.//x" or "/a/..//x" resolve here but to a "//x" path, which a later
    // reader can take for an address on another host.
    return url.origin === origin && !url.pathname.startsWith("//");
  } catch {
    return false;
  }
}

export function setPostLoginRedirect(path: string): void {
  const normalized = normalizeRedirectPath(path);
  if (!normalized || normalized === "/") return;
  sessionStorage.setItem(STORAGE_KEY, normalized);
}

export function consumePostLoginRedirect(): string | null {
  const raw = sessionStorage.getItem(STORAGE_KEY);
  sessionStorage.removeItem(STORAGE_KEY);
  const normalized = normalizeRedirectPath(raw);
  if (!normalized || normalized === "/") return null;
  return normalized;
}

export function buildLoginUrl(path: string): string {
  const normalized = normalizeRedirectPath(path);
  if (!normalized || normalized === "/") return "/login";
  return `/login?redirect=${encodeURIComponent(normalized)}`;
}

// A `/api/*` path is served by the backend, not the SPA router — react-router
// navigate() can't reach it, so the post-login redirect must do a full-page
// navigation (window.location) instead. Used to resume the OAuth consent flow:
// the backend bounces a logged-out user to /login?redirect=/api/oauth/v1/authorize.
export function isBackendRoute(path: string): boolean {
  return path.startsWith("/api/");
}
